/* A native scroll rail stays usable if this enhancement is unavailable. */
(() => {
  if (customElements.get('st-carousel')) return;
  customElements.define('st-carousel', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('.st-carousel__track');
      this.slides = [...this.querySelectorAll('.st-carousel__slide')];
      this.controls = this.querySelector('.st-carousel__controls');
      this.toggle = this.querySelector('[data-carousel-toggle]');
      this.status = this.querySelector('[data-carousel-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.hovered = false;
      this.inView = true;
      this.playing = this.dataset.autoplay === 'true' && !this.motion.matches;
      this.interval = Math.max(5000, Number(this.dataset.interval) || 6000);
      if (!this.track || this.slides.length < 2) return;
      this.controls.hidden = false;
      this.toggle?.addEventListener('pointerdown', () => {
        this.toggleWasPlaying = this.playing;
      }, { signal });
      this.addEventListener('click', event => {
        if (event.target.closest('[data-carousel-prev]')) {
          this.pause(); this.go(this.index - 1);
        } else if (event.target.closest('[data-carousel-next]')) {
          this.pause(); this.go(this.index + 1);
        } else if (event.target.closest('[data-carousel-toggle]')) {
          this.playing = !(this.toggleWasPlaying ?? this.playing);
          this.toggleWasPlaying = undefined;
          this.update(); this.schedule();
        }
      }, { signal });
      this.track.addEventListener('pointerdown', () => this.pause(), { signal, passive: true });
      this.addEventListener('focusin', () => this.pause(), { signal });
      this.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, { signal });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.pause();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal });
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          const first = this.slides[0].offsetLeft;
          const distances = this.slides.map(slide => Math.abs(slide.offsetLeft - first - this.track.scrollLeft));
          this.index = distances.indexOf(Math.min(...distances));
          this.update();
        });
      }, { signal, passive: true });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.pause();
        else this.update();
      }, { signal });
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.slideId === event.detail?.blockId);
        if (index >= 0) { this.pause(); this.go(index, true); }
      }, { signal });
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          this.schedule();
        }, { threshold: .15 });
        this.visibilityObserver.observe(this);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.go(this.index, true));
        this.resizeObserver.observe(this.track);
      }
      this.update();
      this.schedule();
    }
    pause() {
      this.playing = false;
      this.update();
      this.schedule();
    }
    update() {
      const changed = this.lastReportedIndex !== this.index;
      this.lastReportedIndex = this.index;
      this.slides.forEach((slide, index) => { slide.inert = index !== this.index; });
      if (this.status) this.status.textContent = `${this.index + 1} / ${this.slides.length}`;
      if (this.toggle) {
        this.toggle.disabled = this.motion.matches;
        this.toggle.textContent = this.motion.matches ? '自動送りなし' : (this.playing ? '一時停止' : '再生');
        this.toggle.setAttribute('aria-label', this.playing ? 'スライドの自動送りを停止' : 'スライドの自動送りを開始');
      }
      this.track?.setAttribute('aria-live', this.playing ? 'off' : 'polite');
      if (changed) this.dispatchEvent(new CustomEvent('st:slide-change', { bubbles: true, detail: { index: this.index } }));
    }
    go(index, instant = false) {
      if (!this.track || this.slides.length === 0) return;
      this.index = (index + this.slides.length) % this.slides.length;
      const left = this.slides[this.index].offsetLeft - this.slides[0].offsetLeft;
      this.update();
      this.track.scrollTo({ left, behavior: instant || this.motion.matches ? 'instant' : 'smooth' });
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || !this.playing || this.hovered || !this.inView || document.hidden || this.motion.matches || this.slides.length < 2) return;
      this.timer = setTimeout(() => { this.go(this.index + 1); this.schedule(); }, this.interval);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
    }
  });
})();
